import * as React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldQuestion,
  Trash2,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  computeDesignationDiff,
  resolveRoleCeiling,
  userHasPermission,
  type Designation,
} from "@eduviaos/shared";
import { DesignationEditorSheet } from "@/components/DesignationEditorSheet";
import { useDeleteDesignation, useDesignations } from "@/hooks/useDesignations";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useAuth } from "@/auth/AuthProvider";
import { clientFacingText, roleLabelFor } from "@/lib/verticalProfile";

/**
 * Phase F1 — Designations editor (IA Settings → Members & roles).
 *
 * Why a dedicated page instead of an inline modal on the existing Staff
 * tab: the editor sheet is wide enough that it deserves a real surface
 * with its own breadcrumb + a list view that ranks designations by
 * usage. The Members tab will link IN to this page (and IN reverse,
 * the row's "View members" action links to Staff filtered by
 * designation) rather than hosting both views in one cramped sheet.
 *
 * Iron Rule #16: <DataTable> with sticky header + footer, hover-only
 * row highlight, no zebra stripes, action column last + ⋯ dropdown
 * trigger. Row count is small (typical tenant: 5–15 designations) so
 * we render unpaged but keep the footer scaffolding for the day a
 * tenant has > 50.
 */

const QUERY_KEY = "designations-list";

export function DesignationsPage() {
  const [qInput, setQInput] = React.useState("");
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingDesignation, setEditingDesignation] = React.useState<Designation | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<Designation | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  // Phase E1.b.2 — page-level success "flash" banner replaced by the
  // global `<Toaster>` mounted in App.tsx. The editor sheet now fires
  // `toast.success` directly on save (one notification per mutation,
  // not two), and the archive flow toasts here on confirm. The
  // in-dialog `deleteError` banner stays for the destructive-confirm
  // failure path because the structured CONFLICT recovery copy
  // (re-anchor on Members tab) needs to sit beside the dialog buttons
  // so the admin can read it without the toast timing out.

  const { data, isLoading, isFetching, refetch, isError, error } = useDesignations();
  const deleteMutation = useDeleteDesignation();

  // Defense-in-depth gate: the BE owns the authoritative authorization
  // decision, but we hide write affordances from users who lack
  // `manage_roles` so they don't fill out the editor and hit a 403 on
  // save. The seeded `admin` role gets `manage_roles` per TRD-02 §2.3
  // (revised 2026-05-03); this gate matters when a tenant down-grades
  // a designation or invites a `staff`/`teacher` user who somehow
  // routes here directly. Tenant-scoped overrides could grant or
  // revoke `manage_roles` to a single user later — this gate respects
  // those overrides via `userHasPermission()` rather than role alone.
  const { user } = useAuth();
  const { vertical } = useEnabledModules();
  const say = (text: string) => clientFacingText(vertical ?? "school", text);
  const canWrite = React.useMemo(
    () =>
      user
        ? userHasPermission(
            user.role,
            user.permission_overrides ?? [],
            "manage_roles",
            user.permission_mode ?? "additive",
          )
        : false,
    [user],
  );

  const items = React.useMemo<Designation[]>(() => {
    const all = data?.items ?? [];
    const trimmed = qInput.trim().toLowerCase();
    if (!trimmed) return all;
    return all.filter(
      (d) =>
        d.name.toLowerCase().includes(trimmed) ||
        d.role.toLowerCase().includes(trimmed) ||
        (d.description ?? "").toLowerCase().includes(trimmed),
    );
  }, [data?.items, qInput]);

  const total = data?.items.length ?? 0;

  const openCreate = () => {
    setEditingDesignation(null);
    setEditorOpen(true);
  };

  const openEdit = (d: Designation) => {
    setEditingDesignation(d);
    setEditorOpen(true);
  };

  const handleSaved = (_saved: Designation) => {
    // The editor sheet already fires `toast.success` (Phase E1.b.2);
    // we just clear the local edit pointer here so the next "New
    // designation" click opens a clean form.
    setEditingDesignation(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      toast.success(`Archived ${pendingDelete.name}.`);
      setPendingDelete(null);
    } catch (raw) {
      // BE returns 409 ConflictError when the designation is still
      // assigned to staff users. Surface a precise message with a CTA
      // toward the Members tab so the IA can re-anchor the affected
      // users before retrying.
      if (raw instanceof ApiError && raw.code === "CONFLICT") {
        setDeleteError(
          say(
            "This designation is still assigned to one or more staff members. Reassign them from the staff roster, then archive this designation.",
          ),
        );
      } else if (raw instanceof Error && raw.message) {
        setDeleteError(raw.message);
      } else {
        setDeleteError("Could not archive — please try again.");
      }
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8" data-testid={QUERY_KEY}>
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/settings">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Settings
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Designations</PageHeaderHeading>
            <PageHeaderDescription>
              Names for jobs at your {vertical === "study_centre" ? "centre" : "institution"} (for
              example, Senior Counsellor). Each job lists the exact access that person gets, and
              that list can never go above the base role's ceiling.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh designations"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <Button size="sm" onClick={openCreate} data-testid="designations-create">
                <Plus aria-hidden="true" className="size-4" />
                New designation
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {!canWrite && user ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="designations-readonly-banner"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            You're viewing this surface in read-only mode. Authoring and archiving designations
            requires the <code className="text-foreground">manage_roles</code> permission. Ask the
            institution admin or another admin to grant it on the Members tab.
          </span>
        </div>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load designations
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Filter by name, role, or description…"
              className="pl-8"
              aria-label="Filter designations"
              data-testid="designations-filter"
            />
          </div>
          {qInput ? (
            <Button variant="ghost" size="sm" onClick={() => setQInput("")}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Label</TableHead>
                <TableHead className="w-[14%]">Base role</TableHead>
                <TableHead className="w-[14%] text-right">Overrides</TableHead>
                <TableHead className="w-[34%]">Description</TableHead>
                <TableHead className="w-[10%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={5} />
            ) : (
              <TableBody>
                {items.map((d) => (
                  <DesignationRow
                    key={d.id}
                    designation={d}
                    vertical={vertical ?? "school"}
                    canWrite={canWrite}
                    onEdit={openEdit}
                    onArchive={(target) => {
                      setDeleteError(null);
                      setPendingDelete(target);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<ShieldQuestion aria-hidden="true" />}
            title={
              isError ? "Couldn't load designations" : qInput ? "No matches" : "No designations yet"
            }
            description={
              isError
                ? "Refresh to retry — if it keeps failing, check the network."
                : qInput
                  ? "Try a different filter or clear it to see the full list."
                  : say(
                      "Create a designation to name a job and choose the exact access that comes with it.",
                    )
            }
            action={
              isError ? (
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  <RefreshCw aria-hidden="true" className="size-4" />
                  Retry
                </Button>
              ) : qInput ? (
                <Button variant="outline" size="sm" onClick={() => setQInput("")}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filter
                </Button>
              ) : canWrite ? (
                <Button size="sm" onClick={openCreate}>
                  <Plus aria-hidden="true" className="size-4" />
                  New designation
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && total > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}
              {qInput ? " (filtered)" : ""}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">How it works:</strong> the base role is a ceiling. A{" "}
          {say("Teacher")} job can never include access the ceiling does not allow. The editor lists
          the exact access for that job and greys out anything above the ceiling.
        </CardContent>
      </Card>

      <DesignationEditorSheet
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditingDesignation(null);
        }}
        existing={editingDesignation}
        onSaved={handleSaved}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {pendingDelete?.name}?</DialogTitle>
            <DialogDescription>
              {say(
                "The designation is hidden from new staff invites going forward. Existing staff who hold this designation are not affected — they keep their assigned permissions until you move them to another designation from the staff roster.",
              )}
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {deleteError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={deleteMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                "Archive designation"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface DesignationRowProps {
  designation: Designation;
  vertical: string;
  canWrite: boolean;
  onEdit: (d: Designation) => void;
  onArchive: (d: Designation) => void;
}

function DesignationRow({
  designation,
  vertical,
  canWrite,
  onEdit,
  onArchive,
}: DesignationRowProps) {
  const ceiling = React.useMemo(() => resolveRoleCeiling(designation.role), [designation.role]);
  const diff = React.useMemo(
    () => computeDesignationDiff(designation.role, designation.permission_overrides),
    [designation.role, designation.permission_overrides],
  );
  const ceilingSize = ceiling.size;
  const grantedCount = diff.granted.length;

  return (
    <TableRow data-testid={`designation-row-${designation.id}`}>
      <TableCell>
        <div className="text-foreground font-medium">{designation.name}</div>
        <div className="text-muted-foreground mt-0.5 font-mono text-[11px] tracking-tight">
          {designation.id.slice(0, 8)}…
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="font-normal capitalize">
          {roleLabelFor(
            vertical === "study_centre" ? "study_centre" : "school",
            designation.role,
            designation.role.replace(/_/g, " "),
          )}
        </Badge>
      </TableCell>
      <TableCell className="text-right text-xs tabular-nums">
        <div
          className={cn(
            "inline-flex items-baseline gap-1.5",
            grantedCount > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
          )}
        >
          <span className="font-semibold">+{grantedCount.toLocaleString()}</span>
          <span className="text-muted-foreground">/ {ceilingSize.toLocaleString()} ceiling</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {designation.description ? (
          <span className="line-clamp-2 leading-snug">{designation.description}</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </TableCell>
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Actions for ${designation.name}`}
                data-testid={`designation-actions-${designation.id}`}
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem onSelect={() => onEdit(designation)}>
                <Pencil aria-hidden="true" className="size-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onArchive(designation)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="text-muted-foreground/60 text-xs">read-only</span>
        )}
      </TableCell>
    </TableRow>
  );
}
